import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Icon } from '@polichat/flamboyant/dist';
import { useFilterQuickMessage } from 'src/components/chat/content/chat/quickMessages/v2/hooks/useFilterQuickMessage';
import ChannelsService from 'src/services/tools/channels/channels';
import { TriggerSuggestionService } from 'src/services/triggerSuggestion/triggerSuggestionService';
import poliConfig from 'src/utils/poliConfig';

import { getTypeChannel, treatChannel } from 'src/store/modules/chat/functions';

import { fetchQuickMessages } from 'src/store/modules/chat/actions';
import WhatsappMarketingContent from './partials/whatsapp-marketing/whatsapp-marketing';
import { isMobile } from 'react-device-detect';

export const TriggerSuggestionPage = () => {
  const dispatch = useDispatch();

  // Usando useSelector para acessar o estado do Redux
  const chat = useSelector((state) => state.chat);
  const settings = useSelector(
    (state) => state.chat?.config?.general?.settings
  );
  const currentCustomerId = useSelector(
    (state) => state.general?.current_customer_id
  );
  const quickMessages = useSelector((state) => state.chat?.quickMessages);
  const isDarkMode = useSelector((state) => state.theme?.isDarkMode);

  // State para filtros de contatos
  const [filters, setFilters] = useState({
    withoutInteractions: '7days',
    withoutSales: '1month',
  });

  // State para configuração da mensagem
  const [messageConfig, setMessageConfig] = useState({
    channel: null,
    template: null,
    tag: null,
    customMessage: '',
  });

  // State para forçar re-execução do hook useFilterQuickMessage
  const [currentChannelId, setCurrentChannelId] = useState(null);

  // State para contatos sugeridos da API
  const [suggestedContacts, setSuggestedContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsPagination, setContactsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    perPage: 15,
  });
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // State para contatos selecionados
  const [selectedContacts, setSelectedContacts] = useState(new Map());

  // State para controlar o dropdown de templates
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // State para controlar os outros dropdowns
  const [filtersDropdownOpen, setFiltersDropdownOpen] = useState(false);
  const [salesDropdownOpen, setSalesDropdownOpen] = useState(false);
  const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);

  // Mock para botões interativos
  const interactiveButtons = [];

  // Mock para etiquetas
  const tags = [
    { id: 1, name: 'Lead', value: 'lead' },
    { id: 2, name: 'Cliente', value: 'client' },
    { id: 3, name: 'Prospecto', value: 'prospect' },
    { id: 4, name: 'Venda', value: 'sale' },
  ];

  // Adicionando useState para canais
  const [channels, setChannels] = useState([]);

  // Funções de ordenação
  const orderNameChannel = (a, b) => {
    return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
  };

  const orderTypeChannel = (a, b) => {
    const aType =
      a.type === 'waba' ? '1' : a.type === 'whatsapp' ? '2' : a.type;
    const bType =
      b.type === 'waba' ? '1' : b.type === 'whatsapp' ? '2' : b.type;
    return aType < bType ? -1 : aType > bType ? 1 : 0;
  };

  // useEffect para processar canais
  useEffect(() => {
    const _channels = chat?.config?.user?.channels;

    if (Array.isArray(_channels) && _channels.length > 0) {
      const typeWppChannels = ['waba', 'zapi'];

      const channelsWpp = _channels.filter((channel) =>
        typeWppChannels.includes(getTypeChannel(channel))
      );

      // Aplica a função treatChannel para definir ícones e tipos
      const processedChannels = channelsWpp.map((channel) => {
        // Chama treatChannel para adicionar ícones e tipos
        treatChannel(channel);
        return channel;
      });

      const sortedChannels = processedChannels
        .sort(orderNameChannel)
        .sort(orderTypeChannel);
      setChannels(sortedChannels);
    }
  }, [chat?.config?.user?.channels]);

  // Função customizada para buscar canais sem interferir na navegação
  const fetchChannelsCustom = async (customer_id) => {
    try {
      if (customer_id) {
        const result = await ChannelsService.getAllChannels({
          customer_id,
          size: 100,
          isZapiAndWaba: true,
          status: 1,
        });
        // status 1 traz todos que estao ativos
        // status 0 traz todos que estão inativos

        if (result?.data) {
          // Processa os canais recebidos
          const processedChannels = result.data.map((channel) => {
            treatChannel(channel);
            return channel;
          });

          // Ordena os canais
          const sortedChannels = processedChannels
            .sort(orderNameChannel)
            .sort(orderTypeChannel);

          setChannels(sortedChannels);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar canais custom:', error);
    }
  };

  // Função auxiliar para determinar o tipo do canal
  const getTypeChannel = (channel) => {
    // Canais WABA (Business API)
    if (channel.channel_id === 6) return 'waba';

    // Canais Z-API (WhatsApp Business)
    if (channel.channel_id === 1) return 'zapi';

    // Outros tipos
    if (channel.channel_id === 3) return 'facebook';
    if (channel.channel_id === 4) return 'instagram';
    if (channel.channel_id === 5) return 'telegram';
    return 'other';
  };

  // Função para verificar se o canal é WABA ou Z-API
  const isChannelSupported = (channel) => {
    const channelType = getTypeChannel(channel);
    return channelType === 'waba' || channelType === 'zapi';
  };

  // useEffect para carregar canais se necessário
  useEffect(() => {
    if (!chat?.config?.user?.channels && currentCustomerId) {
      fetchChannelsCustom(currentCustomerId);
    }
  }, [currentCustomerId]);

  // useEffect para carregar mensagens rápidas quando a página é montada
  useEffect(() => {
    if (dispatch) {
      dispatch(fetchQuickMessages());
    }
  }, [dispatch]);

  // Função para buscar contatos sugeridos
  const fetchSuggestedContacts = async (page = 1, append = false) => {
    if (page === 1) {
      setContactsLoading(true);
    }

    try {
      // Mapeia os filtros para os parâmetros da API
      const daysSinceLastMessage =
        filters.withoutInteractions === '7days'
          ? '7'
          : filters.withoutInteractions === '15days'
          ? '15'
          : filters.withoutInteractions === '1month'
          ? '30'
          : '30';

      const monthsWithoutPaidSale =
        filters.withoutSales === '1month'
          ? '1'
          : filters.withoutSales === '2months'
          ? '2'
          : filters.withoutSales === '3months'
          ? '3'
          : '1';

      const response = await TriggerSuggestionService.getSuggestedContacts({
        daysSinceLastMessage,
        monthsWithoutPaidSale,
        page,
      });
      if (append) {
        // Adiciona novos contatos à lista existente
        setSuggestedContacts((prev) => [...prev, ...response.data]);
      } else {
        // Substitui a lista de contatos
        setSuggestedContacts(response.data);
      }

      setContactsPagination({
        currentPage: response.meta.current_page,
        totalPages: response.meta.last_page,
        total: response.meta.total,
        perPage: response.meta.per_page,
      });

      // Verifica se há mais páginas
      setHasMorePages(page < response.meta.last_page);
    } catch (error) {
      console.error('Erro ao buscar contatos sugeridos:', error);
      // Em caso de erro, você pode mostrar uma mensagem para o usuário
    } finally {
      setContactsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Função para carregar mais páginas
  const loadMoreContacts = () => {
    if (!isLoadingMore && hasMorePages && !contactsLoading) {
      setIsLoadingMore(true);
      const nextPage = contactsPagination.currentPage + 1;
      fetchSuggestedContacts(nextPage, true);
    }
  };

  // useEffect para carregar contatos sugeridos quando os filtros mudarem
  useEffect(() => {
    setSuggestedContacts([]); // Limpa a lista
    setSelectedContacts(new Map()); // Limpa seleções
    setHasMorePages(true); // Reset do estado de páginas
    fetchSuggestedContacts(1, false);
  }, [filters.withoutInteractions, filters.withoutSales]);

  // useEffect para garantir que o hook useFilterQuickMessage seja re-executado quando o canal mudar
  useEffect(() => {
    setCurrentChannelId(
      messageConfig?.channel?.id ? Number(messageConfig.channel.id) : null
    );
  }, [messageConfig?.channel?.id]);

  // useEffect para buscar quickMessages quando a página carregar
  useEffect(() => {
    dispatch(fetchQuickMessages());
  }, []); // Executa apenas uma vez quando a página monta

  // useEffect para carregar contatos sugeridos quando a página carregar
  useEffect(() => {
    fetchSuggestedContacts(1, false);
  }, []); // Executa apenas uma vez quando a página monta

  // useEffect para buscar quickMessages novamente quando o canal mudar
  useEffect(() => {
    if (messageConfig?.channel?.id) {
      dispatch(fetchQuickMessages());
    }
  }, [messageConfig?.channel?.id, dispatch]);

  // Função para detectar scroll e carregar mais páginas com debounce
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    // Se chegou a 80% do scroll, carrega mais páginas
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      // Debounce para evitar múltiplas chamadas
      if (handleScroll.timeout) {
        clearTimeout(handleScroll.timeout);
      }

      handleScroll.timeout = setTimeout(() => {
        loadMoreContacts();
      }, 300); // 300ms de debounce
    }
  };

  // Função para alternar seleção de um contato
  const toggleContactSelection = (contact) => {
    setSelectedContacts((prev) => {
      const newMap = new Map(prev);
      const contactId = contact.uuid;

      if (newMap.has(contactId)) {
        newMap.delete(contactId);
      } else {
        newMap.set(contactId, contact);
      }
      return newMap;
    });
  };

  // Função para selecionar todos os contatos
  const selectAllContacts = () => {
    const allUuids = suggestedContacts.map((contact) => contact.uuid);
    setSelectedContacts(
      new Map(
        allUuids.map((uuid) => [
          uuid,
          suggestedContacts.find((contact) => contact.uuid === uuid),
        ])
      )
    );
  };

  // Função para deselecionar todos os contatos
  const deselectAllContacts = () => {
    setSelectedContacts(new Map());
  };
  // useFilterQuickMessage
  const channelIdForHook = messageConfig?.channel?.id
    ? Number(messageConfig.channel.id)
    : null;

  // Determina o tipo de mensagem baseado no canal selecionado
  const getMessageType = () => {
    if (!messageConfig.channel) return 'template';
    const channelType = getTypeChannel(messageConfig.channel);

    return channelType === 'waba' ? 'template' : 'quick';
  };

  const { filteredQuickMessages = [] } = useFilterQuickMessage({
    type: getMessageType(),
    text: '',
    channelId: channelIdForHook,
    department_id: null,
    term: '',
  });
  // Função para formatar a mensagem do template
  const formatTemplateMessage = (template) => {
    if (!template) return 'Selecione um template';

    if (template.key && template.originalQuickMessage) {
      const text =
        JSON.parse(template.originalQuickMessage.text)?.body || 'Sem texto';
      return `##${template.originalQuickMessage.tag} ${text.substring(0, 50)}`;
    }

    if (template.isInteractiveButton && template.originalInteractiveButton) {
      const message = template.originalInteractiveButton.message || 'Sem texto';
      return `##${template.originalInteractiveButton.key} ${message.substring(
        0,
        50
      )}`;
    }

    return 'Selecione um template';
  };

  // Função para obter a mensagem de status
  const getStatusMessage = () => {
    if (!messageConfig.channel) {
      return 'Selecione um canal primeiro';
    }

    if (!isChannelSupported(messageConfig.channel)) {
      return 'Canal não suportado';
    }

    return formatTemplateMessage(messageConfig.template);
  };

  // Sem hooks - apenas JSX básico

  const selectOptionsWithoutInteractions = [
    {
      id: 1,
      value: '7days',
      label: '7 dias',
    },
    {
      id: 2,
      value: '15days',
      label: '15 dias',
    },
    {
      id: 3,
      value: '30days',
      label: '30 dias',
    },
  ];

  const selectOptionsWithoutSales = [
    {
      id: 1,
      value: '1month',
      label: '1 mês',
    },
    {
      id: 2,
      value: '2months',
      label: '2 meses',
    },
    {
      id: 3,
      value: '3months',
      label: '3 meses',
    },
  ];

  // Função para obter a cor correta do ícone baseada no tipo do canal
  const getChannelIconColor = (channel) => {
    if (channel.icon_color) {
      return channel.icon_color;
    }

    const channelType = getTypeChannel(channel);
    switch (channelType) {
      case 'waba':
      case 'zapi':
        return 'var(--whatsapp-color)';
      case 'facebook':
        return '#0084F4';
      case 'instagram':
        return '#FFCA5D';
      case 'email':
        return '#ff665d';
      case 'webchat':
        return '#FFCA5D';
      default:
        return 'var(--whatsapp-color)';
    }
  };

  // Função para obter o ícone correto baseado no tipo do canal
  const getChannelIcon = (channel) => {
    if (channel.icon) {
      return channel.icon;
    }

    const channelType = getTypeChannel(channel);
    switch (channelType) {
      case 'waba':
        return 'poli-icon pi-whatsapp-api-fill';
      case 'zapi':
        return 'poli-icon pi-whatsapp-fill';
      case 'facebook':
        return 'poli-icon pi-messenger-fill';
      case 'instagram':
        return 'poli-icon pi-instagram-fill';
      case 'email':
        return 'poli-icon pi-mail-fill';
      case 'webchat':
        return 'poli-icon pi-webchat-fill';
      default:
        return 'poli-icon pi-whatsapp-fill';
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '100px 1fr',
        width: '100%',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          display: isMobile ? 'none' : 'block',
        }}
      ></div>

      <div
        style={{
          padding: '16px',
          paddingTop: '0',
          fontFamily: 'Arial, sans-serif',
          backgroundColor: isDarkMode
            ? 'var(--secondary-background-color)'
            : '#f8f9fa',
          minHeight: '100vh',
          color: isDarkMode ? 'var(--primary-font-color)' : '#333',
        }}
      >
        {/* Header */}
        <div>
          <h1
            style={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: isDarkMode ? 'var(--primary-font-color)' : '#333',
              margin: '0',
            }}
          >
            WhatsApp Marketing
          </h1>

          {/* Tabs */}
          <div
            style={{
              display: 'flex',
              gap: '0',
            }}
          >
            <div
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--primary)',
                color: isDarkMode ? 'var(--white-font-color)' : 'white',
                borderRadius: '5px 5px 0 0',
                fontWeight: '500',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              Sugestão de disparo
            </div>
            {/* <div
              style={{
                padding: '12px 24px',
                backgroundColor: '#e9ecef',
                color: '#6c757d',
                borderRadius: '8px 8px 0 0',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Bot Flow
            </div> */}
          </div>
        </div>

        {/* Main Content */}
        <WhatsappMarketingContent
          chat={chat}
          channels={channels}
          setChannels={setChannels}
          suggestedContacts={suggestedContacts}
          setSuggestedContacts={setSuggestedContacts}
          contactsLoading={contactsLoading}
          setContactsLoading={setContactsLoading}
          contactsPagination={contactsPagination}
          setContactsPagination={setContactsPagination}
          selectedContacts={selectedContacts}
          setSelectedContacts={setSelectedContacts}
          messageConfig={messageConfig}
          setMessageConfig={setMessageConfig}
          currentChannelId={currentChannelId}
          setCurrentChannelId={setCurrentChannelId}
          fetchSuggestedContacts={fetchSuggestedContacts}
          selectAllContacts={selectAllContacts}
          deselectAllContacts={deselectAllContacts}
          handleScroll={handleScroll}
          hasMorePages={hasMorePages}
          filters={filters}
          setFilters={setFilters}
          selectOptionsWithoutInteractions={selectOptionsWithoutInteractions}
          selectOptionsWithoutSales={selectOptionsWithoutSales}
          filtersDropdownOpen={filtersDropdownOpen}
          setFiltersDropdownOpen={setFiltersDropdownOpen}
          salesDropdownOpen={salesDropdownOpen}
          setSalesDropdownOpen={setSalesDropdownOpen}
          channelDropdownOpen={channelDropdownOpen}
          setChannelDropdownOpen={setChannelDropdownOpen}
          dropdownOpen={dropdownOpen}
          setDropdownOpen={setDropdownOpen}
          tagDropdownOpen={tagDropdownOpen}
          setTagDropdownOpen={setTagDropdownOpen}
          interactiveButtons={interactiveButtons}
          filteredQuickMessages={filteredQuickMessages}
          tags={tags}
          isChannelSupported={isChannelSupported}
          isDarkMode={isDarkMode}
        />
      </div>
    </div>
  );
};

export default TriggerSuggestionPage;
